import MgpFormInput from '../../elements/MgpFormInput/MgpFormInput';
import { useForm } from 'react-hook-form';
import MgpButton from '../../elements/MgpButton/MgpButton';
import { useState } from 'react';
import ImageModal from '../../modals/common/ImageModal';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from "yup";
import { ERROR_MESSAGES, MAP_SUBSCRIPTION_MESSAGE, SUBSCRIPTION_ERROR_MESSAGE, SUPPORT_CONTACT } from '../../const/AppConst';
import { calculNirKey, extractJwtToken, getAge, getValueNoSpace, isValidDate } from '../../utils/utils';
import moment from 'moment';
import { AxiosError } from 'axios';
import { IApiErrors, ISubscriptionHandleChangeStep, ISubscriptionInfoV2, MemberStatusType } from '../../context/model';
import { subscriptionSearchPropositionV2 } from '../../services/CustomerApiService';
import LoaderModal from '../../modals/common/LoaderModal';
import SubscriptionLegalNotice from './SubscriptionLegalNotice';
import { baseAppPath } from '../../config/GlobalConfig';
import SubscriptionMessageModal from '../../modals/subscription/SubscriptionMessageModal';


interface SubscriptionFormInfoComponentProps {
  handleChangeStep: (step: number, dataChangeStep: ISubscriptionHandleChangeStep) => void
}

type SubscriptionFormInfoFormData = {
  birthDate: string,
  nir: string,
  nirKey: string,
  administrativeNumber: string
}


export default function SubscriptionFormInfoComponent({
  handleChangeStep
}: Readonly<SubscriptionFormInfoComponentProps>) {

  const [imageUrl, setImageUrl] = useState<string>('');
  const [modalText, setModalText] = useState<string>('');
  const [errorMsg, setErrorMsg] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [blockMessage, setBlockMessage] = useState<string>('');

  const validationSchema = Yup.object().shape({ 
      birthDate: Yup.string()
        .required(ERROR_MESSAGES.requiredMsg)
        .test("validDate", ERROR_MESSAGES.invalidMsg, (val) => {
          let now = new Date();
          now.setHours(0,0,0,0);
          return val !== undefined && isValidDate(val) && new Date(val) < now;
        }),
        nir: Yup.string().required(ERROR_MESSAGES.requiredMsg)
          .min(13, ERROR_MESSAGES.invalidMsg)
          .max(18, ERROR_MESSAGES.invalidMsg)
          .when('birthDate', (birthDate, schema) => {
              if(birthDate && moment(birthDate).isValid()){
                if(getAge(birthDate) >= 16){          
                  return schema.test("follow-birthDate", "La valeur ne correspond pas à sa date de naissance", (val:string) => { 
                    const valueNoSpace = getValueNoSpace(val);

                    const fourthChar = valueNoSpace?.charAt(3);
                    const isProvisoire = valueNoSpace.startsWith("7") || valueNoSpace.startsWith("8");

                    if (fourthChar && Number(fourthChar) > 1) {
                      return true;
                    }
                    if (isProvisoire) {
                      return true;
                    }

                    return valueNoSpace && birthDate.substring(5,7) === valueNoSpace.substring(3,5) && birthDate.substring(2,4) === valueNoSpace.substring(1,3); 
                  }); 
                }
              }        
              return schema;
        }),
        nirKey: Yup.string()
          .required(ERROR_MESSAGES.requiredMsg)
          .min(2, ERROR_MESSAGES.invalidMsg)
          .max(2, ERROR_MESSAGES.invalidMsg)
          .when('nir', (nir, schema) => {
          return schema.test("nir-val", ERROR_MESSAGES.invalidMsg, (val: string) => {
            const valueNoSpace = getValueNoSpace(val);
            const nirNoSpace = getValueNoSpace(nir);
            return valueNoSpace && Number(valueNoSpace) && nirNoSpace && valueNoSpace === calculNirKey(nirNoSpace);
            }); 
        }),
        administrativeNumber: Yup.string()
          .required(ERROR_MESSAGES.requiredMsg)
          .max(15, ERROR_MESSAGES.invalidMsg)
          .matches(/^[a-z0-9 ,.'-]+$/i, ERROR_MESSAGES.invalidMsg)
  }); 
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<SubscriptionFormInfoFormData>({
    mode: "onBlur",
    reValidateMode: "onChange",
    defaultValues: {
 
    },
    resolver: yupResolver(validationSchema),
  });

  const onNextAction = async (data: SubscriptionFormInfoFormData) => {
    try {
      setLoading(true);
      setErrorMsg("");
      const requestBody = {
        birthDate: data.birthDate,
        numberSecurity: getValueNoSpace(data.nir) + data.nirKey.trim(),
        administrativeNumber: data.administrativeNumber
      } as ISubscriptionInfoV2;
      const response = await subscriptionSearchPropositionV2(requestBody);
      const responseData = response?.data;
      setLoading(false);
      if (responseData && responseData.status) {
        if ( responseData.status === MemberStatusType.NOT_FOUND 
              || responseData.status === MemberStatusType.FOUND_NO_AFFILIATION 
              || responseData.status === MemberStatusType.PAPER_FORMULA 
              || responseData.status === MemberStatusType.PROPOSITION_AFFILIATION_OFFICE 
              || responseData.status === MemberStatusType.AFFILIATED_WITH_ACCOUNT 
              || responseData.status === MemberStatusType.WAITING_VALIDATION
        ) {
          const message = MAP_SUBSCRIPTION_MESSAGE.get(responseData.status);
          setBlockMessage(message);
        } else if (responseData.status === MemberStatusType.PROPOSITION_PENDING) {
          if (responseData?.stateCode === 1 
              && (responseData?.statusCode === 3 
              || responseData?.statusCode === 284500019 
              || responseData?.statusCode === 284500007)
          ) {
            const extractedToken = extractJwtToken(responseData.token);

            window.location.href = baseAppPath + `/init?id=${extractedToken?.propositionId}`;
          } else {
            const dataChangeStep = {
              token: responseData.token,
              isShowPhoneNumber: false,
              cacheId: responseData.cacheId
            }
            handleChangeStep(1, dataChangeStep);
          } 
        } else if (responseData.status === MemberStatusType.AFFILIATED_WITHOUT_ACCOUNT) {
          const dataChangeStep = {
            token: responseData.token,
            isShowPhoneNumber: true,
            cacheId: responseData.cacheId
          }
          handleChangeStep(1, dataChangeStep);
        } else {
          setBlockMessage("Vous ne pouvez plus y accédez. Pour toute question complémentaire, veuillez contacter un conseiller.")
        }

      } else {
        const errorMsg = responseData?.errorMessage ?? ERROR_MESSAGES.technicalErrorMsg;
        setErrorMsg(errorMsg);
      }     
    } catch(error) {
      setLoading(false);
      const err = error as AxiosError;
      console.log(err);
      if(err.response && err.response.status === 400) {
        const errorData = err.response?.data as IApiErrors;
        if (errorData.code === "429") {
          setErrorMsg("En raison de la forte affluence sur notre site internet, nous vous recommandons de revenir sur le portail ultérieurement.");
        } else {
          setErrorMsg("Erreur: " + errorData.message);
        }
      } else if(err.response && err.response.status === 404) {
        setErrorMsg(SUBSCRIPTION_ERROR_MESSAGE.NOT_FOUND);
      } else {
        setErrorMsg(ERROR_MESSAGES.technicalErrorMsg);
      }
    }
  }

  const handleOpenInfoModal = (e: any, image: string, text?: string) => {
    e.preventDefault();
    setImageUrl(image);
    if (text) setModalText(text);
  };

  const handleCloseInfoModal = () => {
    setImageUrl('');
    setModalText('');
  };

  const handleCloseModalBlockMessage = () => {
    setBlockMessage('');
  };


  return (             
        <form style={{width: '100%'}}>
            <p className='card-title bleu mb-md'>Renseignez vos données personnelles</p>
             
            {errorMsg && <p className='bg-fushia blanc p-x-lg p-y-md m-y-md text'>{errorMsg}</p>}
            <div className='form-row jc-center'>
                <div className="form-group mw-100">
                  <MgpFormInput
                      label="Date de naissance"
                      id="birthDate"
                      labelFontWeight='regular'
                      required
                      type='date'
                      register={register}
                      errors={errors}
                      min='1900-01-01'
                      max='2099-12-31'
                  />
           
                </div>
            </div>

            <div className='form-row jc-center'>
                <div className="form-group mw-80">
                  <MgpFormInput
                    label="Numéro de sécurité sociale"
                    id="nir"
                    register={register}
                    errors={errors}
                    labelFontWeight='regular'
                    required={true}
                    maxLength={13}
                    tooltipText="Cliquez pour voir les instructions."
                    handleClickInfo={(e: any) => handleOpenInfoModal(e, "Image_AM_CV_entourage_3.jpg", "Il s'agit des 13 premiers numéros présents sur l'attestation de droits ou la carte vitale.")}
                  />
                </div>

                <div className="form-group mw-20">
                  <MgpFormInput
                    label="Clé"
                    id="nirKey"
                    register={register}
                    errors={errors}
                    labelFontWeight='regular'
                    required={true}
                    maxLength={2}
                    tooltipText="Cliquez pour voir les instructions."
                    handleClickInfo={(e: any) => handleOpenInfoModal(e, "Image_AM_CV_entourage_2.jpg", "Il s'agit des deux derniers chiffres de votre numéro de sécurité sociale, composé de 15 chiffres.")}
                  />
                </div>
            </div>

            <div className='form-row jc-center'>
                <div className="form-group mw-100">
                  <MgpFormInput
                    label="Matricule RH"
                    id="administrativeNumber"
                    register={register}
                    errors={errors}
                    labelFontWeight='regular'
                    required
                    tooltipText="Votre matricule est indiqué sur votre carte professionnelle.
                    Si vous ne le retrouvez pas, votre service RH peut vous le fournir."
                    handleClickInfo={(e: any) => e.preventDefault()}
                  />
                </div>
            </div>

            
            <a className="bleu link hide-in-desktop" href={`tel:${SUPPORT_CONTACT}`}>Besoin d'aide ?</a>
           

            <div className='form-row jc-right mt-xl'>
                <MgpButton
                    id="submitButton"
                    type="button"
                    text= {"Suivant"}
                    icon='chevron-right'
                    iconPosition="right"
                    onClick={handleSubmit(onNextAction)}
                ></MgpButton>
                
              </div>

              <SubscriptionLegalNotice />

            { imageUrl && <ImageModal isOpen={true} name={imageUrl} onCloseModal={handleCloseInfoModal} text={modalText}/> }

            { 
              blockMessage 
              && <SubscriptionMessageModal 
                    type='general' 
                    message={blockMessage} 
                    closeAction={handleCloseModalBlockMessage} 
                  /> 
            }

            
            {loading && <LoaderModal hasMessage={true}/>}     
      </form>
  )
}
